import React, {useEffect, useState} from 'react'
import {NavigateFunction, useNavigate} from 'react-router'
import {P} from '../../components/auth/layout'
import {FormRow} from '../../components/form'
import {Button, LinkButton} from '../../components/library/buttons'
import {Loader} from '../../components/loader'
import {useUpdateCurrentAccountMutation} from '../../data/workbench-api/generated/account'
import {getClient} from '../../lib/api'
import {useAuth} from '../../lib/auth'
import {BU_COGNITO_APP_ID} from '../../lib/env'
import {getLocalOrganisationId} from '../../lib/local-store'
import {Input} from '../action-flow/components/library/input'
import {AuthLayout} from './layout'

export const HandleAuthCode = () => {
  const {auth} = useAuth()
  const [errorMessage, setErrorMessage] = useState('')
  const [ready, setReady] = useState(false)
  const code = new URLSearchParams(document.location.search).get('code')
  const clientId = new URLSearchParams(document.location.search).get(
    'client_id'
  )
  const organisationId = getLocalOrganisationId()
  const navigate = useNavigate()

  if (clientId && clientId !== auth.getClientId()) {
    auth.setClientId(clientId)
  }

  useEffect(() => {
    if (auth && code) {
      auth.handleAuthCode(code).then(result => {
        if (result.success) {
          setReady(true)
          return
        }
        console.error(result.error)
        setErrorMessage(result.error)
      })
    }
  }, [auth, code, setErrorMessage])

  if (ready) {
    if (organisationId) {
      return <AssociateAuthWithOrganisation organisationId={organisationId} />
    }
    redirect(navigate)
  }

  if (!code || errorMessage) {
    return (
      <AuthLayout>
        <title>Authentication failed - Atomic Workbench</title>
        <h1>Authentication failed</h1>
        <p>We couldn't log you in</p>
        {auth.getClientId() !== BU_COGNITO_APP_ID ? (
          <FirstTimeLoggingIn />
        ) : null}
        <P>
          <LinkButton size="small" appearance="transparent" to="/">
            Login form
          </LinkButton>
        </P>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <title>Signing in - Atomic Workbench</title>
      <Loader variant="inline" />
    </AuthLayout>
  )
}

const redirect = (navigate: NavigateFunction) => {
  const redirectLocation =
    sessionStorage.getItem('sso_success_redirect_url') ?? '/'
  sessionStorage.removeItem('sso_success_redirect_url')
  navigate(redirectLocation)
}

const FirstTimeLoggingIn = () => {
  const [orgId, setOrgId] = useState<string>('')
  return (
    <>
      <p>
        If this is your first time logging in, please enter your organization ID{' '}
        to continue
      </p>
      <form
        style={{width: '100%'}}
        onSubmit={e => {
          e.preventDefault()
          window.location.href = `/${orgId}`
        }}
      >
        <FormRow>
          <Input
            upLabel="Organization ID"
            label="Enter organization ID..."
            required={true}
            onChange={id => setOrgId(id)}
            value={orgId}
          />
        </FormRow>
        <Button
          type="submit"
          appearance="primary"
          size="small"
          disabled={!orgId}
          style={{
            width: '100%'
          }}
        >
          Continue
        </Button>
      </form>
    </>
  )
}

const AssociateAuthWithOrganisation = ({
  organisationId
}: {
  organisationId: string
}) => {
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()
  const [updateUserAuth] = useUpdateCurrentAccountMutation({
    client: getClient(organisationId),
    variables: {organisationId}
  })

  useEffect(() => {
    updateUserAuth()
      .then(() => {
        // we need to wait for this change to propagate to the routing api
        setTimeout(() => redirect(navigate), 2000)
      })
      .catch(e => {
        console.error(e)
        setErrorMessage(e.message)
      })
  }, [updateUserAuth, navigate])

  if (errorMessage) {
    return (
      <AuthLayout>
        <title>Authorisation failed - Atomic Workbench</title>
        <h1>Authorisation failed</h1>
        <p>You don't have access to organisation {organisationId}</p>
        <P>
          <LinkButton size="small" appearance="transparent" to="/">
            Homepage
          </LinkButton>
        </P>
      </AuthLayout>
    )
  }
  return (
    <AuthLayout>
      <title>Signing in - Atomic Workbench</title>
      <Loader variant="inline" />
    </AuthLayout>
  )
}
